import { Inject, Injectable, InjectionToken } from '@angular/core';
import { pathJoin } from '@asamco/utils';

import { IAppConfig } from './iappconfig.interface';

// environmenter.token.ts
// That last string is just a description for debugging purpose
export const ENVIRONMENT = new InjectionToken('Environment');

/**
 * An interface that extends IAppConfig so we can add your application settings without TS complaining
 * @export
 */
export interface GlobalSetting extends IAppConfig {
	/** app-wide global to retain the last used route, in case we need to return there */
	lastRouteBeforeLogin?: string;
	/** any other data is allowed in here, as long as its hsa a string key */
	[x: string]: any;
}

/**
 * An object that can hold any setting.
 * @export
 */
export interface SettingsObject {
	[key: string]: any;
}

/**
 * Used to get/set global application settings, and access the environment options that are set in the CLI environment.
 */
@Injectable({
	providedIn: 'root',
})
export class AppSettings {
	/**
	 * private object to hold appSettings.
	 *              Settings set in static version can not be overwritten
	 * @ignore
	 */
	private _settings: GlobalSetting = {
		...this.environment.AsamcoAppSettings,
	};

	public readonly deployUrl: string = typeof window !== 'undefined' ? window['deployUrl'] : undefined;

	/**
	 *Creates an instance of AppSettings. and kick off the defaultSettings for devExtreme
	 */
	constructor(@Inject(ENVIRONMENT) private environment: any) {
		this.setDevextremeDefault();
		this.defaultSettings();
	}

	private defaultSettings() {
		const defaults = {};
		this._settings = Object.assign({}, defaults, this.environment.AsamcoAppSettings, window['AsamcoConfig']);
	}

	/**
	 * Get the global application settings, if not set yet. it will
	 * get the settings that are provided in the CLI environment.
	 */
	get settings(): GlobalSetting {
		return this._settings || this.environment.AsamcoAppSettings;
	}

	/**
	 * update app settings in full, can not overwrite settings that come out of ht environment.
	 */
	set settings(update: GlobalSetting) {
		this._settings = Object.assign({}, this._settings, update, this.environment.AsamcoAppSettings);
	}

	/**
	 * Returns the full path to the asset, based on the base url.
	 * If no path was provided, it will return the url to the asset directory.
	 * @example
	 * // If you need the path to 'assets/img/loader.svg'
	 * getAssetUrl('img/loader.svg')
	 *
	 * @param  path
	 * @returns
	 */
	getAssetUrl(path?: string): string {
		const result = pathJoin([this.deployUrl, this.settings.assetsDirectory, path]);

		return result;
	}

	/**
	 * Add or update settings, those will be retained in memory for the duration of the app.
	 * use this if you have a app-wide "global" setting that needs to be available everywhere.
	 * NOTE: The environment settings cannot be overridden! This is done by the setter of the settings property.
	 */
	addOrUpdateSettingsWithObject(o: SettingsObject) {
		this.settings = Object.assign({}, this.environment.AsamcoAppSettings, o);
	}

	/**
	 * Returns whether b2c authentication is enabled
	 */
	get b2cEnabled() {
		return this._settings.b2c && this._settings.b2c.enabled === true;
	}

	/**
	 * Add or update a single setting, those will be retained in memory for the duration of the app.
	 * use this if you have a app-wide "global" setting that needs to be available everywhere.
	 * Note: It's not possible to overwrite environment settings
	 *
	 * @param  key
	 * @param  content
	 */
	addOrUpdateSetting(key: string, content: any) {
		const update = {};
		update[key] = content;

		this.settings = update;
	}

	setDevextremeDefault() {
		// dxDataGrid.defaultOptions({ options: dxDataGridDefaults });
	}
}
